import { useState } from "react";
import { toast } from "sonner";

interface PresignResult {
  fileId: string;
  key: string;
  url: string;
}

/**
 * Client-side upload state machine for the presigned-PUT flow:
 *   1. POST /api/files/presign  → get URL + fileId
 *   2. PUT bytes directly to S3
 *   3. POST /api/files/:id/confirm → flip server status
 *
 * Returns `{ uploadFile, isUploading, progress }`. The hook is non-reactive
 * (no TanStack Query) because each upload is one-shot — caller stores the
 * resulting fileId wherever it needs to.
 */
export function useFileUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);

  async function uploadFile(file: File): Promise<{ fileId: string } | null> {
    setIsUploading(true);
    setProgress(0);
    try {
      const presignRes = await fetch("/api/files/presign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type,
          sizeBytes: file.size,
        }),
      });
      if (!presignRes.ok) {
        const body = await presignRes.json().catch(() => null);
        throw new Error(body?.error ?? "Presign failed");
      }
      const { data } = (await presignRes.json()) as { data: PresignResult };

      const putRes = await fetch(data.url, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type },
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      setProgress(80);

      const confirmRes = await fetch(`/api/files/${data.fileId}/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sizeBytes: file.size }),
      });
      if (!confirmRes.ok) throw new Error("Confirm failed");
      setProgress(100);
      return { fileId: data.fileId };
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Upload failed");
      return null;
    } finally {
      setIsUploading(false);
    }
  }

  return { uploadFile, isUploading, progress };
}

/**
 * Convenience: fetch a presigned-GET download URL for a previously-uploaded
 * file. Lazy — call when the user clicks "View proof". URL expires in 5 min.
 */
export async function fetchFileDownloadUrl(fileId: string): Promise<string | null> {
  const res = await fetch(`/api/files/${fileId}/url`);
  if (!res.ok) return null;
  const { data } = (await res.json()) as { data: { url: string } };
  return data.url;
}
